import type { ReactNode } from 'react';
import { REQUIREMENT_LABEL, type RoleFieldDefinition } from './roleFieldRegistry';
import { useFieldGuidance, type GuidanceState } from './useFieldGuidance';

/**
 * The guidance of one field: an information button next to the label that opens an accessible disclosure with the definition, why EPN
 * asks, an example, whether the field is required, where the data comes from and (sometimes) a help link.
 *
 *  - It opens by click, Enter, Space or touch (a native button). It is never hover-only.
 *  - It closes with Escape (which then returns focus to the button) and does not close the dialog around it.
 *  - The text is real text in the DOM, referenced from the field with `aria-describedby` (`describedById`), also while closed (it is `hidden`).
 *  - The button's name is "About <label>". The name is button content, not an `aria-label`, so a label query for the field still finds
 *    exactly one control.
 *  - The example is guidance only: it is marked as such and is never a placeholder or a value.
 */

export function GuidanceButton({ field, state, buttonRef }: { field: RoleFieldDefinition; state: GuidanceState; buttonRef: (el: HTMLButtonElement | null) => void }) {
  return (
    <button
      ref={buttonRef} type="button" className="jr-info" aria-expanded={state.open} aria-controls={state.panelId} onClick={state.toggle}
      data-testid={`guidance-button-${field.key}`}
    >
      <span aria-hidden="true">i</span>
      <span className="jr-sr-only">About {field.label}</span>
    </button>
  );
}

function Help({ field }: { field: RoleFieldDefinition }) {
  if (!field.helpUrl) return null;
  return (
    <p className="jr-guidance-panel__help">
      <a href={field.helpUrl.href} target="_blank" rel="noopener noreferrer">{field.helpUrl.label}{' '}<span className="jr-sr-only">(opens in a new tab)</span></a>
    </p>
  );
}

export function GuidancePanel({ field, state, extra }: { field: RoleFieldDefinition; state: GuidanceState; extra?: ReactNode }) {
  return (
    <div id={state.panelId} className="jr-guidance-panel" hidden={!state.open} data-testid={`guidance-panel-${field.key}`}>
      <p className="jr-guidance-panel__title">{field.label}</p>
      <dl>
        <dt>What it means</dt>
        <dd>{field.shortDefinition}</dd>
        <dt>Why EPN asks</dt>
        <dd>{field.whyWeAsk}</dd>
        <dt>Example (guidance only, never saved)</dt>
        <dd>{field.example}</dd>
        <dt>Required?</dt>
        <dd>{REQUIREMENT_LABEL[field.requirement]}</dd>
        <dt>Where the value comes from</dt>
        <dd>{field.dataSource}</dd>
      </dl>
      {extra}
      <Help field={field} />
      <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => state.close(true)}>Close help</button>
    </div>
  );
}

/** A stand-alone guidance (button + panel), for a label that is not rendered by GuidedField (the pay band form, the benchmark card). */
export function FieldGuidance({ field }: { field: RoleFieldDefinition }) {
  const { state, setButton } = useFieldGuidance();
  return (
    <span className="jr-guidance" onKeyDown={state.onKeyDown}>
      <GuidanceButton field={field} state={state} buttonRef={setButton} />
      <GuidancePanel field={field} state={state} />
    </span>
  );
}

/** One information button for a small set of related read-only values (Market low, median and high), each with its own definition and why we ask. */
export function FieldGuidanceGroup({ fields, name }: { fields: RoleFieldDefinition[]; name: string }) {
  const { state, setButton } = useFieldGuidance();
  return (
    <span className="jr-guidance" onKeyDown={state.onKeyDown}>
      <button ref={setButton} type="button" className="jr-info" aria-expanded={state.open} aria-controls={state.panelId} onClick={state.toggle}>
        <span aria-hidden="true">i</span>
        <span className="jr-sr-only">About {name}</span>
      </button>
      <div id={state.panelId} className="jr-guidance-panel" hidden={!state.open}>
        <p className="jr-guidance-panel__title">{name}</p>
        <dl>
          {fields.map((f) => (
            <div key={f.key}>
              <dt>{f.label}</dt>
              <dd>{f.shortDefinition} {f.whyWeAsk}</dd>
            </div>
          ))}
          <dt>Where the values come from</dt>
          <dd>{fields[0]?.dataSource}</dd>
        </dl>
        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => state.close(true)}>Close help</button>
      </div>
    </span>
  );
}
